import React, { useEffect, useState } from "react";
import { FileSignature, CheckCircle2, Eye, ShieldCheck, Download } from "lucide-react";
import { PageHead, Loader, EmptyState } from "../components/PageHead";
import { Modal } from "../components/Modal";
import { Badge } from "../components/Badges";
import { fmtDate } from "../lib/format";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Forms() {
  const { can } = useAuth();
  const canAcknowledge = can("forms", "edit");
  const [rows, setRows] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [tab, setTab] = useState("all"); // all | new | acknowledged

  const load = async () => {
    try {
      const r = await api.get("/form-submissions");
      setRows(r.data);
    } catch (e) { toast.error(apiErr(e)); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const filtered = (rows || []).filter((s) => {
    if (tab === "new") return s.status === "submitted";
    if (tab === "acknowledged") return s.status === "acknowledged_by_pte";
    return true;
  });

  const counts = {
    all: (rows || []).length,
    new: (rows || []).filter((s) => s.status === "submitted").length,
    acknowledged: (rows || []).filter((s) => s.status === "acknowledged_by_pte").length,
  };

  return (
    <>
      <PageHead crumb="Records" title="Signed Forms"
        sub="All customer form submissions — Contract Reviews and other acknowledgements. Click a row to view the signature and acknowledge." />

      <div className="card pad" style={{ marginBottom: 14, display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="forms-tabs">
        {[
          { key: "all", label: "All" },
          { key: "new", label: "New" },
          { key: "acknowledged", label: "Acknowledged" },
        ].map((t) => (
          <button key={t.key} className={`btn sm ${tab === t.key ? "gold" : "ghost"}`}
            onClick={() => setTab(t.key)} data-testid={`forms-tab-${t.key}`}>
            {t.label} <span className="t-mut" style={{ marginLeft: 6 }}>{counts[t.key]}</span>
          </button>
        ))}
      </div>

      {rows === null && <Loader />}
      {rows && filtered.length === 0 && (
        <EmptyState icon={FileSignature}>No submissions in this view.</EmptyState>
      )}

      {rows && filtered.length > 0 && (
        <div className="card pad" style={{ overflow: "hidden" }}>
          <div className="tbl-wrap">
            <table data-testid="forms-table">
              <thead>
                <tr>
                  <th>Form</th>
                  <th>Customer</th>
                  <th>Signed by</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} data-testid={`form-row-${s.id}`}>
                    <td>
                      <span className="cell-2">
                        <span className="t-strong" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><FileSignature size={13} /> {s.form_title}</span>
                        <span className="s mono t-mut">{s.form_code} · rev {s.form_version}</span>
                      </span>
                    </td>
                    <td>
                      <span className="cell-2">
                        <span className="t-strong">{s.customer_name || "—"}</span>
                        {s.customer_number && <span className="s mono t-mut">{s.customer_number}</span>}
                      </span>
                    </td>
                    <td>
                      <span className="cell-2">
                        <span className="t-strong">{s.signature_name}</span>
                        <span className="s mono t-mut">{s.submitted_by_email}</span>
                      </span>
                    </td>
                    <td className="mono">{fmtDate(s.submitted_at)}</td>
                    <td>
                      {s.status === "acknowledged_by_pte"
                        ? <Badge label="Acknowledged" cls="b-green" />
                        : <Badge label="New" cls="b-amber" />}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <button className="btn sm ghost" onClick={() => setViewing(s)} data-testid={`form-view-${s.id}`}><Eye size={13} /> View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {viewing && (
        <SubmissionView
          submissionId={viewing.id}
          onClose={() => setViewing(null)}
          canAcknowledge={canAcknowledge}
          onAcknowledged={() => { setViewing(null); load(); }}
        />
      )}
    </>
  );
}

export function SubmissionView({ submissionId, onClose, canAcknowledge, onAcknowledged }) {
  const [sub, setSub] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const s = await api.get(`/form-submissions/${submissionId}`);
        if (cancel) return;
        setSub(s.data);
        const f = await api.get(`/forms/${s.data.form_id}`);
        if (!cancel) setForm(f.data);
      } catch (e) { toast.error(apiErr(e)); }
    })();
    return () => { cancel = true; };
  }, [submissionId]);

  const acknowledge = async () => {
    setBusy(true);
    try {
      await api.post(`/form-submissions/${submissionId}/acknowledge`);
      toast.success("Acknowledged");
      onAcknowledged && onAcknowledged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} wide icon={FileSignature}
      title={form?.title || "Signed form"}
      subtitle={sub ? `${sub.form_code} · ${sub.customer_name}` : "Loading…"}
      footer={<>
        <button className="btn ghost" onClick={onClose}>Close</button>
        {sub?.has_pdf || sub?.has_signature_image ? (
          <button className="btn ghost" onClick={async () => {
            try {
              const token = localStorage.getItem("pte_token");
              const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/form-submissions/${submissionId}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
              if (!resp.ok) throw new Error("PDF unavailable");
              const blob = await resp.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href = url; a.download = `${sub.form_code}-${sub.customer_number || "submission"}.pdf`; a.click();
              window.URL.revokeObjectURL(url);
            } catch (e) { toast.error(e.message); }
          }} data-testid="form-pdf-download">
            <Download size={14} /> Download PDF
          </button>
        ) : null}
        {canAcknowledge && sub?.status !== "acknowledged_by_pte" && (
          <button className="btn gold" onClick={acknowledge} disabled={busy} data-testid="acknowledge-form">
            <CheckCircle2 size={14} /> {busy ? "Saving…" : "Acknowledge"}
          </button>
        )}
        {sub?.status === "acknowledged_by_pte" && (
          <Badge label={`Acknowledged by ${sub.acknowledged_by_name || "PTE"} · ${fmtDate(sub.acknowledged_at)}`} cls="b-green" />
        )}
      </>}>
      {!sub || !form ? <Loader /> : (
        <>
          <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 14, fontSize: 12.5 }}>
            <div><strong>Customer:</strong> {sub.customer_name} {sub.customer_number ? <span className="mono">({sub.customer_number})</span> : null}</div>
            <div><strong>Submitted by:</strong> {sub.submitted_by_name} · <span className="mono">{sub.submitted_by_email}</span></div>
            <div><strong>Date:</strong> {fmtDate(sub.submitted_at)}</div>
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            {(form.schema || []).filter((f) => {
              if (f.section) return true;
              if (!f.show_if) return true;
              return sub.data[f.show_if.key] === f.show_if.equals;
            }).map((f, idx) => {
              if (f.section) {
                return (
                  <div key={`sec-${idx}`} style={{ marginTop: idx === 0 ? 0 : 10, paddingBottom: 6, borderBottom: "1px solid var(--border)" }}>
                    <div className="t-strong" style={{ fontSize: 12.5, letterSpacing: ".03em", textTransform: "uppercase", color: "var(--gold-deep)" }}>{f.section}</div>
                  </div>
                );
              }
              let val = sub.data[f.key];
              if (f.type === "radio" && val) {
                const opt = f.options?.find((o) => o.value === val);
                if (opt) val = opt.label;
              }
              return (
                <div key={f.key} style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 12, fontSize: 13 }}>
                  <div className="t-mut">{f.label}</div>
                  <div className="t-strong" data-testid={`view-field-${f.key}`}>{val || <span className="t-mut">—</span>}</div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 22 }}>
            <div className="t-strong" style={{ fontSize: 12.5, letterSpacing: ".03em", textTransform: "uppercase", color: "var(--gold-deep)", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid var(--border)" }}>
              Signature
            </div>
            <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 14, background: "#fff" }}>
              {sub.signature_dataurl ? (
                <img src={sub.signature_dataurl} alt="signature" style={{ maxHeight: 120, maxWidth: "100%" }} data-testid="submission-signature" />
              ) : <span className="t-mut">No signature image</span>}
              <div className="s t-mut" style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <ShieldCheck size={12} /> Signed by <strong style={{ marginLeft: 4 }}>{sub.signature_name}</strong>{sub.signature_date && <> on {sub.signature_date}</>}
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
